import React from 'react'

import BaseModal from '../BaseModal2'
import HorizontalToggle from '../../../atoms/HorizontalToggle'
import EndControls from '../EndControls'
import ModalTextField from '../../../atoms/ModalTextField'
import ChipAutocomplete from '../../../atoms/ChipAutocomplete'

import useDelayedUserSearch from '../../../../hooks/useDelayedUserSearch'
import { formatSessionForPut } from '../../../../utilities'
import { updateSession } from '../../../../lib/api'
import useAccess, { PERMS } from '../../../../hooks/useAccess'
import { useQueueState } from '../../../../providers/QueueStateProvider'
import { useUserAPI } from '../../../../providers/UserAPIProvider'
import { useAppDispatch } from '../../../../hooks/redux'
import { AddErrorAlert } from '../../../../slices/alert'

import useStyles from './styles'

const CLIENTS = 'Clients'
const NEW_CLIENT = 'New Client'
const REMOVE_CLIENT = 'Remove from Client'

const OPTIONS = [CLIENTS, NEW_CLIENT]

const MoveConversationModal = ({ open, onClose, assigning, convo }) => {
  const dispatch = useAppDispatch()
  const { conversation } = useQueueState()
  const { uploadUser } = useUserAPI()
  const { search } = useDelayedUserSearch(true)
  const hasPermission = useAccess()

  const [selected, setSelected] = React.useState(CLIENTS)
  const [person, setPerson] = React.useState(null)
  const [name, setName] = React.useState('')

  const current = React.useMemo(
    () => formatSessionForPut(conversation),
    [conversation]
  )

  const classes = useStyles()

  const clearVals = React.useCallback(() => {
    setPerson(null)
    setName('')
    setSelected(CLIENTS)
  }, [])

  const showError = React.useCallback(
    message => dispatch(AddErrorAlert({ err: { message } })),
    [dispatch]
  )

  const handleMove = React.useCallback(async () => {
    let newOwnerID = null

    if (selected === NEW_CLIENT) {
      try {
        const newUser = {
          name,
          role_id: null,
          avatar_id: null,
          channels: [],
          status_id: null
        }
        const created = await new Promise((res, rej) =>
          uploadUser(newUser, false, res)
        )
        newOwnerID = created.id
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
        return
      }
    } else if (selected === CLIENTS) {
      if (person) {
        newOwnerID = person.id
      } else {
        showError('Need to select a user')
        return
      }
    }

    const newSession = {
      ...current,
      owner_id: newOwnerID,
      assigned_to: current.assigned_to ?? null
    }

    const { id, ...rest } = newSession

    delete rest.userIds
    delete rest.verified

    try {
      await updateSession(id, rest)
      clearVals()
      onClose()
    } catch (err) {
      console.error('Error moving conversation', err)
      dispatch(AddErrorAlert({ err }))
    }
  }, [
    selected,
    name,
    uploadUser,
    person,
    showError,
    current,
    clearVals,
    onClose,
    dispatch
  ])

  const options = React.useMemo(() => {
    const copy = [...OPTIONS]

    if (convo) {
      const { owner_id, locked } = convo
      if (owner_id && !locked && hasPermission(PERMS.ConversationsFull)) {
        copy.push(REMOVE_CLIENT)
      }
    }

    return copy
  }, [hasPermission, convo])

  return (
    <>
      {open && (
        <BaseModal
          open={open}
          onClose={() => {
            clearVals()
            onClose()
          }}
          title={assigning ? 'ASSIGN TO CUSTOMER' : 'MOVE CONVERSATION'}
          PaperProps={{ style: { top: '53%', width: '50%' } }}
        >
          <HorizontalToggle
            name='owner_type'
            options={options}
            selected={selected}
            onChange={val => {
              if (val === CLIENTS) {
                search(name)
              }
              setSelected(val)
            }}
          />

          {selected !== REMOVE_CLIENT && (
            <div className={classes.root}>
              {selected === NEW_CLIENT && (
                <ModalTextField title='Name' className={classes.input}>
                  <input
                    placeholder='New client name'
                    value={name}
                    onChange={e => setName(e.target.value)}
                    autoFocus
                  />
                </ModalTextField>
              )}

              {selected !== NEW_CLIENT && (
                <ModalTextField title='Client Name'>
                  {/* TODO: Check this when this modal is in use */}
                  <ChipAutocomplete
                    value={person}
                    onChange={setPerson}
                    single
                    autoFocus
                  />
                </ModalTextField>
              )}
            </div>
          )}

          <EndControls
            onSubmit={handleMove}
            submitText={
              assigning
                ? 'ASSIGN'
                : selected !== REMOVE_CLIENT
                ? 'MOVE'
                : 'REMOVE FROM CLIENT'
            }
            showExtraIcons={false}
            SubmitIcon={() => null}
          />
        </BaseModal>
      )}
    </>
  )
}

export default MoveConversationModal
